import type { ReactNode } from "react"
import type React from "react"
import { Table, TableBody, TableCell, TableHeader, TableRow } from "./index"
import { LoadState } from "../LoadState"
import { EmptyState } from "../EmptyState"

export type DataTableColumn<T> = {
  key: string
  label: string
  /** Accessible name for the header cell when `label` is empty (e.g. an icon-only or checkbox column). */
  headerAriaLabel?: string
  sortable?: boolean
  align?: "left" | "center" | "right"
  render?: (row: T) => ReactNode
}

export type DataTableSort = { column: string; dir: "asc" | "desc" } | null

type DataTableProps<T> = {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  loading?: boolean
  emptyMessage?: string
  sort?: DataTableSort
  onSortChange?: (sort: DataTableSort) => void
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  /** Si se pasa, muestra un selector de tamaño de página con estas opciones. */
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]
}

const alignClass = { left: "text-left", center: "text-center", right: "text-right" } as const

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

/** Ventana deslizante de hasta 5 números de página centrada en `page`, con `null` como marcador de elipsis. */
function pageWindow(page: number, totalPages: number): (number | null)[] {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)

  const window = new Set<number>([1, totalPages, page])
  for (let offset = 1; offset <= 1 && window.size < 5; offset++) {
    if (page - offset >= 1) window.add(page - offset)
    if (page + offset <= totalPages) window.add(page + offset)
  }
  while (window.size < 5) {
    const max = Math.max(...window)
    const min = Math.min(...window)
    if (max < totalPages) window.add(max + 1)
    else if (min > 1) window.add(min - 1)
    else break
  }

  const sorted = [...window].sort((a, b) => a - b)
  const result: (number | null)[] = []
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null)
    result.push(sorted[i])
  }
  return result
}

export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyMessage = "No se encontraron registros.",
  sort = null,
  onSortChange,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: DataTableProps<T>) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const pageNumbers = pageWindow(page, totalPages)

  function toggleSort(column: DataTableColumn<T>) {
    if (!column.sortable || !onSortChange) return
    if (sort?.column !== column.key) {
      onSortChange({ column: column.key, dir: "asc" })
    } else if (sort.dir === "asc") {
      onSortChange({ column: column.key, dir: "desc" })
    } else {
      onSortChange({ column: column.key, dir: "asc" })
    }
  }

  // Columna sin `label` (ej. checkbox o el menú de acciones "...") se trata como
  // esquina superior de la tarjeta en móvil, no como otra fila label:valor.
  const cardCornerColumn = columns.find((c) => !c.label)
  const cardBodyColumns = columns.filter((c) => c !== cardCornerColumn)
  const [cardTitleColumn, ...cardRestColumns] = cardBodyColumns
  const sortableColumns = columns.filter((c) => c.sortable)

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
      {sortableColumns.length > 0 && onSortChange ? (
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 sm:hidden dark:border-gray-800">
          <label className="flex min-h-11 flex-1 items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="shrink-0">Ordenar por</span>
            <select
              value={sort ? `${sort.column}:${sort.dir}` : ""}
              onChange={(e) => {
                if (!e.target.value) return
                const [column, dir] = e.target.value.split(":") as [string, "asc" | "desc"]
                onSortChange({ column, dir })
              }}
              className="min-h-11 flex-1 rounded-lg border border-gray-300 bg-white px-2 text-sm dark:border-gray-700 dark:bg-gray-900"
            >
              {!sort && <option value="">Sin orden</option>}
              {sortableColumns.map((column) => (
                <optgroup key={column.key} label={column.label}>
                  <option value={`${column.key}:asc`}>{column.label} ▲</option>
                  <option value={`${column.key}:desc`}>{column.label} ▼</option>
                </optgroup>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {rows.length === 0 && loading ? (
        <LoadState />
      ) : rows.length === 0 ? (
        <EmptyState message={emptyMessage} />
      ) : (
        <div aria-busy={loading} className={`transition-opacity ${loading ? "opacity-60" : ""}`}>
          {/* Tarjetas apiladas en móvil: la misma data de `columns`/`rows`, sin scroll horizontal. */}
          <div className="divide-y divide-gray-100 sm:hidden dark:divide-gray-800">
            {rows.map((row) => (
              <div key={rowKey(row)} className="flex items-start justify-between gap-3 p-4">
                <div className="min-w-0 flex-1 space-y-1.5">
                  {cardTitleColumn ? (
                    <div className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {cardTitleColumn.render
                        ? cardTitleColumn.render(row)
                        : String((row as Record<string, unknown>)[cardTitleColumn.key] ?? "")}
                    </div>
                  ) : null}
                  {cardRestColumns.map((column) => (
                    <div key={column.key} className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="shrink-0 text-xs text-gray-500 dark:text-gray-500">{column.label}</span>
                      <span className="min-w-0 text-right text-gray-600 dark:text-gray-300">
                        {column.render ? column.render(row) : String((row as Record<string, unknown>)[column.key] ?? "")}
                      </span>
                    </div>
                  ))}
                </div>
                {cardCornerColumn ? (
                  <div className="shrink-0">
                    {cardCornerColumn.render
                      ? cardCornerColumn.render(row)
                      : String((row as Record<string, unknown>)[cardCornerColumn.key] ?? "")}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="hidden overflow-x-auto sm:block">
            <Table>
              <TableHeader className="border-b border-gray-200 dark:border-gray-800">
                <TableRow>
                  {columns.map((column) => {
                    const isSorted = sort?.column === column.key
                    const ariaSort: React.AriaAttributes["aria-sort"] = !column.sortable
                      ? undefined
                      : isSorted
                        ? sort!.dir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    return (
                      <TableCell
                        key={column.key}
                        isHeader
                        scope="col"
                        aria-sort={ariaSort}
                        aria-label={!column.label ? column.headerAriaLabel : undefined}
                        className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 ${alignClass[column.align ?? "left"]} ${
                          column.sortable ? "cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200" : ""
                        }`}
                      >
                        <button
                          type="button"
                          disabled={!column.sortable}
                          onClick={() => toggleSort(column)}
                          className="inline-flex min-h-11 items-center gap-1 disabled:cursor-default"
                        >
                          {column.label}
                          {column.sortable && (
                            <span aria-hidden="true" className="text-[11px] leading-none">
                              {isSorted ? (sort!.dir === "asc" ? "▲" : "▼") : "⇅"}
                            </span>
                          )}
                        </button>
                      </TableCell>
                    )
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={rowKey(row)}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                  >
                    {columns.map((column) => (
                      <TableCell key={column.key} className={`px-4 py-3 text-sm ${alignClass[column.align ?? "left"]}`}>
                        {column.render ? column.render(row) : String((row as Record<string, unknown>)[column.key] ?? "")}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400">
        <div className="flex items-center gap-3">
          <span>
            {total === 0 ? (loading ? "Cargando..." : "0 registros") : `${total} registro${total === 1 ? "" : "s"} · página ${page} de ${totalPages}`}
          </span>
          {onPageSizeChange && (
            <label className="flex items-center gap-1.5 text-xs">
              <span className="sr-only">Registros por página</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="min-h-11 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size} / página
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="min-h-11 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium disabled:opacity-40 dark:border-gray-700"
          >
            Anterior
          </button>
          <div className="hidden items-center gap-2 sm:contents">
            {pageNumbers.map((n, i) =>
              n === null ? (
                <span key={`ellipsis-${i}`} aria-hidden="true" className="px-1 text-xs text-gray-500 dark:text-gray-400">
                  …
                </span>
              ) : (
                <button
                  key={n}
                  type="button"
                  aria-current={n === page ? "page" : undefined}
                  onClick={() => onPageChange(n)}
                  className={`min-h-11 min-w-11 rounded-lg border px-3 py-1.5 text-xs font-medium ${
                    n === page
                      ? "border-brand-500 bg-brand-500 text-white dark:border-brand-500 dark:bg-brand-500"
                      : "border-gray-300 dark:border-gray-700"
                  }`}
                >
                  {n}
                </button>
              ),
            )}
          </div>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="min-h-11 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium disabled:opacity-40 dark:border-gray-700"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  )
}
